import React, { useState } from 'react';
import { Plus, Trash2, AlertTriangle, Clock } from 'lucide-react';

const SegmentEditor = ({ 
  timeSegments = [], 
  onAddSegment, 
  onRemoveSegment, 
  onUpdateSegment,
  videoDuration = 0
}) => {
  const [error, setError] = useState(null);
  const [timeInputs, setTimeInputs] = useState({});
  const MAX_TOTAL_DURATION = 120; // 2 minutes in seconds
  
  // Calculate total duration of all segments
  const totalDuration = timeSegments.reduce((sum, segment) => {
    return sum + (segment.end - segment.start);
  }, 0);
  
  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Parse time string from MM:SS format to seconds
  const parseTimeString = (timeStr) => {
    try {
      if (!timeStr) return 0;
      
      // If input contains a colon, treat as MM:SS
      if (timeStr.includes(':')) {
        const parts = timeStr.split(':');
        if (parts.length === 2) {
          const mins = parseInt(parts[0], 10) || 0;
          const secs = parseInt(parts[1], 10) || 0;
          return mins * 60 + secs;
        }
      }
      
      // Otherwise treat as raw seconds
      return parseInt(timeStr, 10) || 0;
    } catch (e) {
      return 0;
    }
  };
  
  // Check if adding this segment would exceed the total limit
  const wouldExceedLimit = (index, field, value, currentSegments) => {
    // Create a copy of the segments
    const updatedSegments = [...currentSegments];
    
    // Update the segment being changed
    const updatedSegment = {...updatedSegments[index]};
    updatedSegment[field] = value;
    
    // If start time is after end time, adjust end time
    if (field === 'start' && value >= updatedSegment.end) {
      updatedSegment.end = Math.min(value + 10, videoDuration || 600);
    }
    
    updatedSegments[index] = updatedSegment;
    
    // Calculate new total duration
    const newTotalDuration = updatedSegments.reduce((sum, segment) => {
      return sum + (segment.end - segment.start);
    }, 0);
    
    return newTotalDuration > MAX_TOTAL_DURATION;
  };
  
  // Handle direct text input 
  const handleInputChange = (index, field, value) => {
    // Store the raw input value
    setTimeInputs({
      ...timeInputs,
      [`${index}-${field}`]: value
    });
  };

  // Handle blur to process the input when user stops typing
  const handleInputBlur = (index, field, value) => {
    let seconds;
    
    // Parse different input formats
    if (value.includes(':')) {
      // MM:SS format
      const [minutes, secs] = value.split(':');
      seconds = (parseInt(minutes) || 0) * 60 + (parseInt(secs) || 0);
    } else if (value.length <= 2) {
      // Just seconds (e.g. "23" means 0:23)
      seconds = parseInt(value) || 0;
    } else if (value.length > 2) {
      // More than 2 digits - interpret as M:SS
      // e.g. "123" means 1:23
      const minutes = parseInt(value.slice(0, value.length - 2)) || 0;
      const secs = parseInt(value.slice(value.length - 2)) || 0;
      seconds = minutes * 60 + secs;
    } else {
      seconds = 0;
    }

    const effectiveVideoDuration = videoDuration || 600;
    
    // Validate time value
    if (seconds < 0) {
      setError("Time cannot be negative");
      return;
    }
    
    if (videoDuration > 0 && seconds > videoDuration) {
      setError(`Time cannot exceed video length (${formatTime(videoDuration)})`);
      setTimeInputs({
        ...timeInputs,
        [`${index}-${field}`]: formatTime(field === 'start' ? 0 : effectiveVideoDuration)
      });
      return;
    }
    
    // Get the other field value for comparison
    const otherField = field === 'start' ? 'end' : 'start';
    const otherValue = timeSegments[index][otherField];
    
    // If this would make the segment invalid
    if ((field === 'start' && seconds >= otherValue) || 
        (field === 'end' && seconds <= timeSegments[index].start)) {
      setError(field === 'start' ? 
        "Start time must be before end time" : 
        "End time must be after start time");
      
      // Reset to valid value
      setTimeInputs({
        ...timeInputs,
        [`${index}-${field}`]: formatTime(timeSegments[index][field])
      });
      return;
    }
    
    // Check if this change would exceed the total duration limit
    if (wouldExceedLimit(index, field, seconds, timeSegments)) {
      setError(`Total duration of all segments cannot exceed 2 minutes (${totalDuration} seconds used)`);
      setTimeInputs({
        ...timeInputs,
        [`${index}-${field}`]: formatTime(timeSegments[index][field])
      });
      return;
    }
    
    // All is good, update normally
    setError(null);
    onUpdateSegment(index, field, seconds);
    
    // Update input to show formatted time
    setTimeInputs({
      ...timeInputs,
      [`${index}-${field}`]: formatTime(seconds)
    });
  };
  
  // Handle adding a new segment
  const handleAddSegment = () => {
    // Default 10 second segment
    const defaultDuration = 10;
    const effectiveVideoDuration = videoDuration || 600;
    
    // If we already have segments, start the new one after the last one ends
    let startTime = 0;
    if (timeSegments.length > 0) {
      const lastSegment = timeSegments[timeSegments.length - 1];
      startTime = Math.min(lastSegment.end, effectiveVideoDuration - defaultDuration);
    }
    
    const endTime = Math.min(startTime + defaultDuration, effectiveVideoDuration);
    
    // Check if adding this segment would exceed the total duration limit
    const newSegment = { start: startTime, end: endTime };
    const newTotalDuration = totalDuration + (endTime - startTime);
    
    if (newTotalDuration > MAX_TOTAL_DURATION) {
      setError(`Cannot add segment: would exceed 2 minute total limit (${totalDuration} seconds already used)`);
      return;
    }
    
    setError(null);
    onAddSegment(newSegment);
  };

  // Get input value (either from state or formatted from segment)
  const getInputValue = (index, field) => {
    const stateKey = `${index}-${field}`;
    if (timeInputs[stateKey] !== undefined) {
      return timeInputs[stateKey];
    }
    return formatTime(timeSegments[index][field]);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <h2 className="text-lg font-semibold mb-3">Select Time Segments</h2>
      
      <div className="mb-4 bg-blue-50 p-3 rounded-md flex justify-between items-center">
        <div className="flex items-center text-sm text-blue-700">
          <Clock size={16} className="mr-1" /> 
          <span>Video length: {videoDuration > 0 ? formatTime(videoDuration) : "Loading..."}</span>
        </div>
        
        <div className="flex items-center text-sm">
          <span className={totalDuration > MAX_TOTAL_DURATION ? "text-red-600 font-bold" : "text-green-600"}>
            Total selected: {totalDuration} / {MAX_TOTAL_DURATION} seconds
          </span>
        </div>
      </div>
      
      {error && (
        <div className="mb-4 bg-yellow-50 p-3 rounded-md flex items-center text-sm">
          <AlertTriangle className="text-yellow-500 mr-2" size={16} />
          <span className="text-yellow-700">{error}</span>
        </div>
      )}
      
      <div className="space-y-4">
        {timeSegments.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-500 mb-2">No segments added yet</p>
            <button
              onClick={handleAddSegment}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md"
            >
              <Plus size={16} className="mr-1" />
              Add First Segment
            </button>
          </div>
        ) : (
          <>
            {timeSegments.map((segment, index) => {
              const duration = segment.end - segment.start;
              return (
                <div key={index} className="p-4 border rounded-md bg-blue-50">
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <label className="block text-sm font-medium mb-1">START TIME:</label>
                      <input
                        type="text"
                        value={getInputValue(index, 'start')}
                        onChange={(e) => handleInputChange(index, 'start', e.target.value)}
                        onBlur={(e) => handleInputBlur(index, 'start', e.target.value)}
                        className="w-full p-2 border rounded text-center"
                        placeholder="0:00"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Type seconds (23) or minutes:seconds (1:23)
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">END TIME:</label>
                      <input
                        type="text"
                        value={getInputValue(index, 'end')}
                        onChange={(e) => handleInputChange(index, 'end', e.target.value)}
                        onBlur={(e) => handleInputBlur(index, 'end', e.target.value)}
                        className="w-full p-2 border rounded text-center"
                        placeholder="0:00"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Type seconds (23) or minutes:seconds (1:23)
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-sm px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                      Segment Duration: {duration} seconds
                    </span>
                    
                    <button
                      onClick={() => onRemoveSegment(index)}
                      className="flex items-center px-2 py-1 text-white bg-red-600 rounded-md hover:bg-red-700"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
            
            <button
              onClick={handleAddSegment}
              disabled={totalDuration >= MAX_TOTAL_DURATION}
              className={`w-full flex items-center justify-center p-2 ${
                totalDuration >= MAX_TOTAL_DURATION
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              } text-white rounded-md`}
            >
              <Plus size={18} className="mr-2" />
              Add Another Segment
            </button>
          </>
        )}
      </div>
      
      {timeSegments.length > 0 && (
        <div className="mt-4 bg-gray-50 p-3 rounded-md">
          <p className="text-sm text-gray-600">
            <strong>Time Input Tips:</strong>
            <ul className="mt-1 list-disc pl-5">
              <li>Type seconds only: <strong>23</strong> for 0:23</li>
              <li>Type 3 digits: <strong>123</strong> for 1:23</li>
              <li>Type with colon: <strong>1:23</strong> for 1:23</li>
              <li>All segments combined: max 2 minutes</li>
            </ul>
          </p>
        </div>
      )}
    </div>
  );
};

export default SegmentEditor;
